// File: src/components/RecommendedTrades.js  (FULL DROP-IN, PERMISSION-SAFE)
// ✅ Still uses PublicTrades only for listings (safe)
// ✅ Keeps: AI/weights/scoring/logging/inferredFromBehavior + carousel UI
// ✅ Supports header URL filters:
//    - ?q=
//    - ?category=
//    - ?cond=used|new|all
// ✅ Filters recommended results by category + condition + search term
// ✅ Keeps guest + signed-in scoring behavior
// ✅ Permission-safe analytics writes only to:
//    /userInteractions/{uid}/recommendations/{docId}

import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { db } from "../firebase";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import "./RecommendedTrades.css";

import { getTradeImageSrc, handleImgError } from "../utils/images";

const DEFAULT_WEIGHTS = {
  category: 2,
  style: 2,
  condition: 1,
  tradeType: 1,
  location: 1,
  brand: 1,
};

const NEW_WINDOW_HOURS = 72;
const BEST_MATCH_SCORE = 6;

const MAX_FETCH = 320;
const MAX_SHOW = 20;
const PREFETCH_LIMIT = 8;
const REFRESH_EVERY_MS = 0;

const iconMap = {
  category: "📦",
  style: "🎨",
  condition: "💠",
  tradetype: "🔁",
  tradeType: "🔁",
  location: "📍",
  brand: "🏷️",
  budget: "💰",
  unique: "⭐",
  uniqueness: "⭐",
};

const normalizeReasonKey = (reason) => String(reason || "").toLowerCase().replace(/\s|_/g, "");

const labelForReason = (reasonKey) => {
  const map = {
    category: "Category",
    style: "Style",
    condition: "Condition",
    tradetype: "Trade Type",
    tradeType: "Trade Type",
    location: "Location",
    brand: "Brand",
    budget: "Budget",
    unique: "Unique",
    uniqueness: "Unique",
  };
  return map[reasonKey] || "Match";
};

const safeLower = (v) => (typeof v === "string" ? v.toLowerCase() : "");

const pickCashValue = (trade) => {
  const candidates = [
    trade?.expectedCashAmount,
    trade?.cashExpected,
    trade?.expectedCash,
    trade?.cash_expectation,
    trade?.cashPrice,
    trade?.cashprice,
    trade?.cashAdd,
    trade?.cash_add,
    trade?.price,
    trade?.estimatedValue,
  ];
  for (const v of candidates) {
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
};

const formatMoneyZAR = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return "";
  return `R${num.toLocaleString("en-ZA")}`;
};

const formatMoneyCompact = (n, currency = "R") => {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return "";
  if (num >= 1_000_000) return `${currency}${Math.round(num / 100_000) / 10}M`;
  if (num >= 1000) return `${currency}${Math.round(num / 100) / 10}k`;
  return `${currency}${num}`;
};

const getCreatedDate = (trade) => {
  const ts = trade?.createdAt ?? trade?.created_at ?? trade?.created;
  if (!ts) return null;
  if (typeof ts.toDate === "function") return ts.toDate();
  if (typeof ts === "string") {
    const d = new Date(ts);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof ts === "number") {
    const d = new Date(ts);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const isNewTrade = (trade) => {
  const d = getCreatedDate(trade);
  if (!d) return false;
  const diffMs = Date.now() - d.getTime();
  return diffMs >= 0 && diffMs <= NEW_WINDOW_HOURS * 60 * 60 * 1000;
};

const formatShortDate = (trade) => {
  const d = getCreatedDate(trade);
  if (!d) return "";
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
};

const buildWhyText = (trade) => {
  const reasons = Array.isArray(trade?.matchReasons) ? trade.matchReasons : [];
  if (!reasons.length) return "Recommended based on general similarity.";
  const labels = reasons.slice(0, 6).map((r) => labelForReason(normalizeReasonKey(r)));
  return `Recommended because it matches your: ${labels.join(", ")}.`;
};

const detectRegionFromLanguage = () => {
  const lang = (typeof navigator !== "undefined" && navigator.language) || "";
  const parts = lang.split("-");
  const region = parts.length >= 2 ? parts[1].toUpperCase() : "";
  return region || null;
};

const extractTradeCountrySignals = (trade) => {
  const raw =
    trade?.countryCode ??
    trade?.country ??
    trade?.locationCountry ??
    trade?.location?.countryCode ??
    trade?.location?.country ??
    trade?.geo?.countryCode ??
    trade?.geo?.country;

  const sigs = [];
  if (typeof raw === "string" && raw.trim()) sigs.push(raw.trim());
  if (typeof trade?.location === "string" && trade.location.trim()) sigs.push(trade.location.trim());
  return sigs;
};

const isCountryMatch = (trade, regionCode) => {
  if (!regionCode) return false;
  const rc = regionCode.toUpperCase();
  const sigs = extractTradeCountrySignals(trade).map((s) => s.toUpperCase());
  if (sigs.some((s) => s === rc)) return true;

  const common = {
    ZA: ["SOUTH AFRICA", "RSA"],
    US: ["UNITED STATES", "USA", "AMERICA"],
    GB: ["UNITED KINGDOM", "UK", "GREAT BRITAIN", "ENGLAND"],
    AE: ["UNITED ARAB EMIRATES", "UAE"],
    SA: ["SAUDI ARABIA"],
  };

  const names = common[rc] || [];
  if (names.length && sigs.some((s) => names.some((n) => s.includes(n)))) return true;
  if (sigs.some((s) => s.includes(rc))) return true;
  return false;
};

const getBrowserCoords = () =>
  new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return resolve(null);

    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }),
      () => resolve(null),
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 60_000 }
    );
  });

const extractTradeCoords = (trade) => {
  const lat =
    trade?.geo?.lat ??
    trade?.geo?.latitude ??
    trade?.locationGeo?.lat ??
    trade?.locationGeo?.latitude ??
    trade?.lat ??
    trade?.latitude;

  const lng =
    trade?.geo?.lng ??
    trade?.geo?.longitude ??
    trade?.locationGeo?.lng ??
    trade?.locationGeo?.longitude ??
    trade?.lng ??
    trade?.longitude;

  const a = Number(lat);
  const b = Number(lng);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return { lat: a, lng: b };
};

const haversineKm = (a, b) => {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const sLat1 = (a.lat * Math.PI) / 180;
  const sLat2 = (b.lat * Math.PI) / 180;

  const x =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(sLat1) * Math.cos(sLat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);

  return 2 * R * Math.asin(Math.sqrt(x));
};

const tradeBelongsToUser = (trade, uid) => {
  if (!uid) return false;

  const ownerCandidates = [
    trade?.sellerUid,
    trade?.ownerUid,
    trade?.ownerId,
    trade?.userId,
    trade?.uid,
    trade?.createdByUid,
    trade?.postedByUid,
    trade?.seller?.uid,
    trade?.owner?.uid,
    trade?.createdBy?.uid,
  ]
    .map((v) => String(v || "").trim())
    .filter(Boolean);

  return ownerCandidates.includes(String(uid).trim());
};

const matchesSearch = (trade, term) => {
  if (!term) return true;
  const q = String(term).trim().toLowerCase();
  if (!q) return true;

  const fields = [
    trade?.title,
    trade?.description,
    trade?.condition,
    trade?.categoryLabel,
    trade?.category,
    trade?.categoryKey,
    trade?.brand,
    trade?.location?.city,
    trade?.location?.region,
    trade?.location?.country,
    trade?.location?.label,
  ];

  return fields.some((v) => String(v ?? "").toLowerCase().includes(q));
};

const getConditionKey = (trade) => {
  const raw =
    trade?.condition ??
    trade?.itemCondition ??
    trade?.conditionLabel ??
    trade?.conditionNorm ??
    (typeof trade?.isNew === "boolean" ? (trade.isNew ? "new" : "used") : "") ??
    "";

  const k = safeLower(raw);
  if (!k) return "";
  if (k === "new" || k.includes("brand new")) return "new";
  if (k === "used" || k.includes("pre-owned") || k.includes("preowned") || k.includes("second")) return "used";
  if (["good", "fair", "poor", "ok", "okay", "excellent"].includes(k)) return "used";
  return k;
};

const matchesCondition = (trade, cond) => {
  const c = safeLower(cond);
  if (!c || c === "all") return true;
  const key = getConditionKey(trade);
  if (!key) return false;
  return key === c;
};

const matchesCategory = (trade, category) => {
  const c = String(category || "").trim().toLowerCase();
  if (!c) return true;

  const fields = [
    trade?.category,
    trade?.categoryLabel,
    trade?.categoryKey,
    trade?.categoryNorm,
  ].map((v) => String(v || "").trim().toLowerCase());

  return fields.some((v) => v === c || v.includes(c));
};

const keyForGroup = (t) => {
  const cat = safeLower(t?.categoryNorm || t?.category || t?.categoryKey || "");
  const city = safeLower(t?.location?.city || "");
  return `${cat}::${city}`;
};

const diversifyTopN = (arr, n = 20) => {
  const buckets = new Map();
  arr.forEach((t) => {
    const k = keyForGroup(t) || "other";
    if (!buckets.has(k)) buckets.set(k, []);
    buckets.get(k).push(t);
  });

  const keys = Array.from(buckets.keys());
  keys.sort((a, b) => (buckets.get(b).length || 0) - (buckets.get(a).length || 0));

  const out = [];
  while (out.length < n) {
    let progressed = false;
    for (const k of keys) {
      const b = buckets.get(k);
      if (b && b.length) {
        out.push(b.shift());
        progressed = true;
        if (out.length >= n) break;
      }
    }
    if (!progressed) break;
  }
  return out;
};

const prefetchTradeRoute = (tradeId) => {
  try {
    const link = document.createElement("link");
    link.rel = "prefetch";
    link.href = `/trade/${tradeId}`;
    document.head.appendChild(link);
    setTimeout(() => {
      try {
        document.head.removeChild(link);
      } catch {}
    }, 5000);
  } catch {}
};

const preloadImage = (src) =>
  new Promise((resolve) => {
    try {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = src;
    } catch {
      resolve(false);
    }
  });

const RecommendedTrades = ({
  searchTerm = "",
  refreshKey = null,
  onToast = null,
  uid: uidProp = null,
  setLoading: setLoadingProp = null,
}) => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const uid = uidProp || currentUser?.uid || null;
  const isGuest = !uid;

  const [recommendations, setRecommendations] = useState([]);
  const [usedAISuggestions, setUsedAISuggestions] = useState(false);

  const [detectedRegion, setDetectedRegion] = useState(null);
  const [coords, setCoords] = useState(null);

  const [contextTradeTitle, setContextTradeTitle] = useState("");
  const [seenIds, setSeenIds] = useState(() => new Set());

  const scrollRef = useRef(null);
  const cardRefs = useRef(new Map());
  const observerRef = useRef(null);

  const setLoading = useCallback(
    (v) => {
      if (typeof setLoadingProp === "function") setLoadingProp(v);
    },
    [setLoadingProp]
  );

  const urlFilters = useMemo(() => {
    try {
      const sp = new URLSearchParams(location.search || "");
      const q = String(sp.get("q") || "").trim();
      const category = String(sp.get("category") || "").trim();
      const condRaw = String(sp.get("cond") || "").trim().toLowerCase();
      const cond = condRaw === "new" || condRaw === "used" ? condRaw : "all";
      return { q, category, cond };
    } catch {
      return { q: "", category: "", cond: "all" };
    }
  }, [location.search]);

  const effectiveSearch = useMemo(() => {
    const direct = String(searchTerm || "").trim();
    if (direct) return direct;
    return urlFilters.q;
  }, [searchTerm, urlFilters.q]);

  const logUserEvent = useCallback(
    async (eventType, payload) => {
      if (!uid) return;
      try {
        await db
          .collection("userInteractions")
          .doc(uid)
          .collection("recommendations")
          .add({
            eventType,
            ...payload,
            ts: new Date().toISOString(),
          });
      } catch {}
    },
    [uid]
  );

  useEffect(() => {
    setDetectedRegion(detectRegionFromLanguage());
  }, []);

  useEffect(() => {
    let alive = true;
    getBrowserCoords().then((c) => {
      if (!alive) return;
      setCoords(c);
    });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!uid) {
      setContextTradeTitle("");
      return;
    }

    (async () => {
      try {
        const prefSnap = await db.collection("preferences").doc(uid).get();
        if (!prefSnap.exists) return;

        const pref = prefSnap.data() || {};
        const rv = Array.isArray(pref.recentViews) ? pref.recentViews : [];
        const first = rv[0] ? String(rv[0]) : "";
        if (!first) return;

        const tSnap = await db.collection("PublicTrades").doc(first).get();
        if (!tSnap.exists) return;

        const t = tSnap.data() || {};
        setContextTradeTitle(String(t.title || "").slice(0, 52));
      } catch {}
    })();
  }, [uid]);

  const fetchRecommendedTrades = useCallback(async () => {
    setLoading(true);

    try {
      const snap = await db.collection("PublicTrades").orderBy("createdAt", "desc").limit(MAX_FETCH).get();
      const allTrades = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const trades = allTrades
        .filter((t) => !tradeBelongsToUser(t, uid))
        .filter((t) => matchesSearch(t, effectiveSearch))
        .filter((t) => matchesCategory(t, urlFilters.category))
        .filter((t) => matchesCondition(t, urlFilters.cond));

      if (!uid) {
        const region = detectedRegion;

        const scored = trades.map((t) => {
          let s = 0;

          const created = getCreatedDate(t);
          if (created) {
            const ageH = (Date.now() - created.getTime()) / (1000 * 60 * 60);
            if (ageH <= 24) s += 3;
            else if (ageH <= 72) s += 2;
            else if (ageH <= 168) s += 1;
          }

          if (region && isCountryMatch(t, region)) s += 2;

          if (coords) {
            const tc = extractTradeCoords(t);
            if (tc) {
              const km = haversineKm(coords, tc);
              if (km <= 25) s += 3;
              else if (km <= 80) s += 2;
              else if (km <= 250) s += 1;
            }
          }

          if (pickCashValue(t) != null) s += 0.5;

          return { ...t, matchScore: s, matchReasons: [] };
        });

        scored.sort((a, b) => (b.matchScore ?? 0) - (a.matchScore ?? 0));
        setRecommendations(diversifyTopN(scored, MAX_SHOW));
        setUsedAISuggestions(false);
        return;
      }

      const prefSnap = await db.collection("preferences").doc(uid).get();
      if (!prefSnap.exists) {
        setRecommendations([]);
        return;
      }

      let prefs = prefSnap.data() || {};

      let appliedAI = false;
      const aiSnap = await db.collection("aiSuggestions").doc(uid).get();
      if (aiSnap.exists) {
        const aiData = aiSnap.data() || {};
        const aiPrefs = aiData.suggestedPreferences || {};
        const aiWeights = aiData.recommendedWeights || {};
        const shouldApplyAI = !prefs.preferencesCompleted || !!aiData.forceApply;

        if (shouldApplyAI) {
          appliedAI = true;
          prefs = {
            ...prefs,
            ...aiPrefs,
            matchingWeights: Object.keys(aiWeights).length ? aiWeights : prefs.matchingWeights,
          };
        }
      }
      setUsedAISuggestions(appliedAI);

      const weights = prefs.matchingWeights || DEFAULT_WEIGHTS;

      const preferredStylesArray = Array.isArray(prefs.preferredStyles)
        ? prefs.preferredStyles
        : prefs.preferredStyles
        ? [prefs.preferredStyles]
        : [];

      const preferredBrandsArray = Array.isArray(prefs.preferredBrands)
        ? prefs.preferredBrands
        : prefs.preferredBrands
        ? [prefs.preferredBrands]
        : [];

      const favoriteCategoriesArray = Array.isArray(prefs.favoriteCategories)
        ? prefs.favoriteCategories
        : prefs.favoriteCategories
        ? [prefs.favoriteCategories]
        : [];

      const tradeTypeArray = Array.isArray(prefs.tradeType) ? prefs.tradeType : prefs.tradeType ? [prefs.tradeType] : [];
      const prefLoc = prefs.location;

      const matches = trades.map((trade) => {
        let score = 0;
        const reasons = [];

        const tradeCategory = trade.category ?? trade.categoryLabel ?? trade.categoryKey ?? null;

        if (tradeCategory && favoriteCategoriesArray.includes(tradeCategory)) {
          score += weights.category;
          reasons.push("category");
        }

        if (
          preferredStylesArray.length > 0 &&
          preferredStylesArray.some((style) => safeLower(trade.title).includes(safeLower(style)))
        ) {
          score += weights.style;
          reasons.push("style");
        }

        if (
          preferredBrandsArray.length > 0 &&
          preferredBrandsArray.some((brand) => safeLower(trade.title).includes(safeLower(brand)))
        ) {
          score += weights.brand;
          reasons.push("brand");
        }

        if (!prefs.preferredCondition || prefs.preferredCondition === "Any" || trade.condition === prefs.preferredCondition) {
          score += weights.condition;
          reasons.push("condition");
        }

        const tt = trade.tradeType ?? trade.tradePreference ?? "";
        if (tradeTypeArray.includes(tt)) {
          score += weights.tradeType;
          reasons.push("tradeType");
        }

        if (coords) {
          const tc = extractTradeCoords(trade);
          if (tc) {
            const km = haversineKm(coords, tc);
            if (km <= 25) {
              score += weights.location + 2;
              reasons.push("location");
            } else if (km <= 80) {
              score += weights.location + 1;
              reasons.push("location");
            } else if (km <= 250) {
              score += weights.location;
              reasons.push("location");
            }
          }
        }

        if (!reasons.includes("location") && detectedRegion && isCountryMatch(trade, detectedRegion)) {
          score += weights.location;
          reasons.push("location");
        }

        const tradeLoc = trade.location;

        const cityMatch =
          prefLoc?.cities?.some(
            (city) =>
              typeof tradeLoc === "string" &&
              typeof city === "string" &&
              tradeLoc.toLowerCase().includes(city.toLowerCase())
          ) || false;

        const textLocMatch =
          typeof prefLoc === "string" &&
          typeof tradeLoc === "string" &&
          tradeLoc.toLowerCase().includes(prefLoc.toLowerCase());

        if (!reasons.includes("location") && (cityMatch || textLocMatch)) {
          score += weights.location;
          reasons.push("location");
        }

        if (pickCashValue(trade) != null) score += 0.75;

        return { ...trade, matchScore: score, matchReasons: reasons };
      });

      let filtered = matches.filter((t) => (t.matchScore ?? 0) >= 3);
      if (filtered.length === 0) filtered = matches.filter((t) => (t.matchScore ?? 0) >= 1);
      if (filtered.length === 0) filtered = matches.slice(0, 5);

      filtered.sort((a, b) => (b.matchScore ?? 0) - (a.matchScore ?? 0));

      const diversified = diversifyTopN(filtered, MAX_SHOW);
      setRecommendations(diversified);

      const batch = db.batch();
      diversified.forEach((match) => {
        const ref = db.collection("userInteractions").doc(uid).collection("recommendations").doc();
        batch.set(ref, {
          eventType: "recommendation",
          tradeId: match.id,
          category: match.category ?? match.categoryLabel ?? match.categoryKey ?? null,
          brandMatch: (match.matchReasons || []).includes("brand"),
          score: match.matchScore || 0,
          timestamp: new Date().toISOString(),
          source: appliedAI ? "AI" : "Manual",
        });
      });
      await batch.commit();

      const categoryCount = {};
      const brandMatches = [];
      const styles = [];

      diversified.forEach((match) => {
        const cat = match.category ?? match.categoryLabel ?? match.categoryKey ?? null;
        if (cat) categoryCount[cat] = (categoryCount[cat] || 0) + 1;
        if ((match.matchReasons || []).includes("brand")) brandMatches.push(match.title);
        if ((match.matchReasons || []).includes("style")) styles.push(match.title);
      });

      const topCategories = Object.entries(categoryCount)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([cat]) => cat);

      await db.collection("aiSuggestions").doc(uid).set(
        {
          suggestedPreferences: {
            favoriteCategories: topCategories,
            preferredBrands: brandMatches.filter(Boolean).slice(0, 5),
            preferredStyles: styles.length > 0 ? styles[0] : "",
          },
          recommendedWeights: weights,
          inferredFromBehavior: true,
          lastUpdated: new Date().toISOString(),
        },
        { merge: true }
      );
    } catch (err) {
      console.error("❌ Failed to fetch recommendations:", err);
      if (typeof onToast === "function") onToast("Unable to load recommendations right now. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  }, [uid, setLoading, detectedRegion, coords, effectiveSearch, urlFilters.category, urlFilters.cond, onToast]);

  useEffect(() => {
    fetchRecommendedTrades();
  }, [fetchRecommendedTrades, refreshKey]);

  useEffect(() => {
    if (!REFRESH_EVERY_MS || REFRESH_EVERY_MS < 10_000) return undefined;
    const t = setInterval(() => fetchRecommendedTrades(), REFRESH_EVERY_MS);
    return () => clearInterval(t);
  }, [fetchRecommendedTrades]);

  const onCardHover = useCallback(async (trade) => {
    if (!trade?.id) return;
    prefetchTradeRoute(trade.id);

    const src = getTradeImageSrc(trade);
    if (src) await preloadImage(src);
  }, []);

  useEffect(() => {
    if (observerRef.current) {
      try {
        observerRef.current.disconnect();
      } catch {}
    }

    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const tradeId = e.target?.getAttribute?.("data-tradeid");
          if (!tradeId) return;

          setSeenIds((prev) => {
            if (prev.has(tradeId)) return prev;
            const next = new Set(prev);
            next.add(tradeId);
            return next;
          });

          logUserEvent("impression", {
            tradeId,
            component: "RecommendedTrades",
          });

          if (!prefersReduced) {
            try {
              e.target.classList.add("recSeen");
            } catch {}
          }
        });
      },
      { root: null, threshold: 0.55 }
    );

    observerRef.current = obs;

    const els = Array.from(cardRefs.current.values()).filter(Boolean);
    els.forEach((el) => obs.observe(el));

    return () => {
      try {
        obs.disconnect();
      } catch {}
    };
  }, [recommendations, logUserEvent]);

  const scroll = (direction) => {
    const el = scrollRef.current || document.querySelector(".recommended-scroll");
    if (!el) return;
    const amount = Math.round(el.clientWidth * 0.85);
    el.scrollBy({ left: direction === "left" ? -amount : amount, behavior: "smooth" });
  };

  const subtitle = useMemo(() => {
    const filterBits = [];
    if (urlFilters.category) filterBits.push(urlFilters.category);
    if (urlFilters.cond && urlFilters.cond !== "all") filterBits.push(urlFilters.cond);

    const filterText = filterBits.length ? ` • Filtered by: ${filterBits.join(" • ")}` : "";
    const because = contextTradeTitle ? ` • Because you viewed: “${contextTradeTitle}”` : "";

    if (isGuest) return `Popular trades across the platform — sign in to personalize${filterText}.`;

    return usedAISuggestions
      ? `Top picks — curated using your preferences (AI-assisted)${because}${filterText}`
      : `Top picks — curated using your preferences${because}${filterText}`;
  }, [isGuest, usedAISuggestions, contextTradeTitle, urlFilters.category, urlFilters.cond]);

  const onClickTrade = useCallback(
    async (trade) => {
      if (!uid || !trade?.id) return;
      await logUserEvent("click", {
        tradeId: trade.id,
        component: "RecommendedTrades",
        score: trade.matchScore ?? null,
      });
    },
    [uid, logUserEvent]
  );

  return (
    <section className={`recommended-section recommended-wrapper ${isGuest ? "is-guest" : ""}`}>
      <div className="recommended-header">
        <div>
          <h3 className="recommended-title">{isGuest ? "Top Picks" : "Recommended for You"}</h3>
          <p className="recommended-subtitle">{subtitle}</p>
        </div>

        {!isGuest && usedAISuggestions && (
          <span className="recommended-pill" title="AI suggestions were applied to improve matching">
            AI-assisted
          </span>
        )}

        {isGuest && (
          <Link to="/login" className="recommended-ctaBtn">
            Sign in for personalized
          </Link>
        )}
      </div>

      {recommendations.length === 0 ? (
        <div className="recommended-empty">
          <div className="recommended-empty-title">{isGuest ? "No matching top picks right now" : "No matching recommendations right now"}</div>
          <div className="recommended-empty-sub">
            Try changing the selected category, condition, or search term.
          </div>
        </div>
      ) : (
        <div className="recommended-carousel">
          <button className="recommended-arrow left" onClick={() => scroll("left")} aria-label="Scroll left" type="button">
            ‹
          </button>

          <div className="recommended-scroll" ref={scrollRef}>
            {recommendations.map((trade, idx) => {
              const cash = pickCashValue(trade);
              const cashText = cash ? formatMoneyCompact(cash, "R") : "";
              const dateText = formatShortDate(trade);

              const isCash = trade?.tradeType ? String(trade.tradeType).toLowerCase().includes("cash") : false;
              const dealLabel = trade?.tradeType ? (isCash ? "Cash" : "Swap") : "Deal";

              const bestMatch = !isGuest && (trade.matchScore ?? 0) >= BEST_MATCH_SCORE;
              const reasonsToShow = !isGuest
                ? (trade.matchReasons?.length ? trade.matchReasons : ["unique"]).slice(0, 4)
                : [];

              const seen = seenIds.has(trade.id);

              return (
                <Link
                  to={`/trade/${trade.id}`}
                  key={trade.id}
                  className={`recommended-card trade-card ${bestMatch ? "isBestMatch" : ""} ${seen ? "isSeen" : ""}`}
                  title={trade.title || "View trade"}
                  onMouseEnter={() => idx < PREFETCH_LIMIT && onCardHover(trade)}
                  onFocus={() => idx < PREFETCH_LIMIT && onCardHover(trade)}
                  onClick={() => onClickTrade(trade)}
                  ref={(el) => {
                    if (!el) return;
                    cardRefs.current.set(trade.id, el);
                  }}
                  data-tradeid={trade.id}
                >
                  <div className="recommended-image trade-image-wrapper">
                    <img src={getTradeImageSrc(trade)} alt={trade.title || "Trade"} onError={handleImgError} loading="lazy" />
                    <div className="recImgShade" aria-hidden="true" />

                    {bestMatch && (
                      <span className="recBestMatch" title="Best match">
                        👑 Best match
                      </span>
                    )}

                    {isNewTrade(trade) && (
                      <span className="recNewBadge" title="New listing">
                        New
                      </span>
                    )}

                    {cashText ? (
                      <span className="recImgPrice" title={`Cash price: ${formatMoneyZAR(cash)}`}>
                        {cashText}
                      </span>
                    ) : null}

                    <span className={`recImgCondition ${trade?.condition === "New" ? "isNew" : "isUsed"}`}>
                      {trade?.condition || "Used"}
                    </span>
                    {dateText ? <span className="recImgDate">{dateText}</span> : null}

                    <div className="recHoverOverlay" aria-hidden="true">
                      <span className="recHoverCta">{seen ? "View again" : "View trade"}</span>
                    </div>

                    {seen && (
                      <span className="recSeenPill" title="You've seen this">
                        Seen
                      </span>
                    )}
                  </div>

                  <div className="tradeCardBody">
                    <div className="tradeCardTitleRow">
                      <div className="tradeCardTitle">{trade.title || "Untitled trade"}</div>

                      <div className="recScoreWrap">
                        {!isGuest && (
                          <span className="recommended-score" title="Match score">
                            {trade.matchScore ?? 0}
                          </span>
                        )}

                        {!isGuest && (
                          <span className="recWhy" tabIndex={0} aria-label="Why recommended">
                            ?
                            <span className="recWhyTip">{buildWhyText(trade)}</span>
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="tradeCardMeta">
                      <span className={`tradeCardPref ${isCash ? "isCash" : "isSwap"}`}>{dealLabel}</span>

                      {cashText ? (
                        <span className="tradeCardMiniHint" title="Cash amount is optional for swap trades">
                          Cash allowed
                        </span>
                      ) : (
                        !isGuest && <span className="tradeCardMiniHint">Picked for you</span>
                      )}
                    </div>

                    {!isGuest && (
                      <div className="recommended-reasons">
                        <div className="reason-tags-inline">
                          {reasonsToShow.map((reason, index) => {
                            const key = normalizeReasonKey(reason);
                            const icon = iconMap[key] || "⭐";
                            return (
                              <span
                                key={`${key}-${index}`}
                                className={`reason-icon reason-${key}`}
                                title={`Matched by ${labelForReason(key)}`}
                                style={{ animationDelay: `${index * 0.05}s` }}
                              >
                                {icon}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>

          <button className="recommended-arrow right" onClick={() => scroll("right")} aria-label="Scroll right" type="button">
            ›
          </button>
        </div>
      )}
    </section>
  );
};

export default RecommendedTrades;